import { useState } from 'react';
import { Badge } from '../../../components/ui/Badge';
import { ChevronDownIcon, ChevronRightIcon } from '../../../components/icons/Icons';
import { formatDate } from '../../../lib/format';
import type { AutoReplyInteraction } from '../../../types/api';

/** `messageStatus` -> how it reads on the screen. Unknown statuses fall back to the raw value. */
function statusBadge(status: string) {
  if (status === 'rejected') return <Badge variant="orange">Rejected</Badge>;
  if (status === 'failed') return <Badge variant="orange">Failed</Badge>;
  return <Badge variant="neutral">{status}</Badge>;
}

function RejectedItem({ entry }: { entry: AutoReplyInteraction }) {
  const reply = entry.reply;

  return (
    <article className="rejected-item">
      <div className="thread__head">
        {statusBadge(entry.messageStatus)}
        <span className="thread__who">
          {entry.senderEmail ?? 'Unknown sender'}
          {entry.senderName ? ` · ${entry.senderName}` : ''}
        </span>
        <span className="thread__when">{formatDate(entry.receivedAt)}</span>
      </div>

      {entry.subject ? <p className="thread__subject">{entry.subject}</p> : null}
      {entry.outcomeCode ? <p className="thread__meta">{entry.outcomeCode}</p> : null}
      {entry.securityFlags.length > 0 ? (
        <p className="thread__meta">{entry.securityFlags.join(', ')}</p>
      ) : null}
      {reply?.errorCode ? (
        <p className="thread__error">
          {reply.errorCode}
          {reply.errorDetail ? `: ${reply.errorDetail}` : ''}
        </p>
      ) : null}
      {entry.bodyPreview ? <pre className="thread__body">{entry.bodyPreview}</pre> : null}
    </article>
  );
}

export function RejectedEmailsPanel({ entries }: { entries: AutoReplyInteraction[] }) {
  const [expanded, setExpanded] = useState(false);

  if (entries.length === 0) return null;

  return (
    <section className="panel">
      <div className="panel__body">
        <button
          type="button"
          className="rejected-toggle"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
        >
          {expanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
          <h3 className="detail-section-title">Rejected &amp; failed emails ({entries.length})</h3>
        </button>

        {expanded ? (
          <div className="rejected-list">
            {entries.map((entry) => (
              <RejectedItem key={entry.messageId} entry={entry} />
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}
